/**
 * ADM-ROLES.WIDGET
 * Set permissions for account
 */

import { store } from "@/index"
import { withTranslation, WithTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import {
  userActivate,
  userDestroy,
  userEditAndUpdateRole,
  userFetch,
  userFetchCount,
  userGet,
  userRemove,
  userStatus,
  userUpdatePasswordProvided_AsAdmin,
  userUpdateRole
} from "@/redux/user.actions"
import { User, UserState } from "@/redux/user.types"
import { connect } from "react-redux"
import ListItem from "@/components/list-item"
import ListButton from "@/components/list-button"
import ListCell from "@/components/list-cell"
import {
  faCrown,
  faEye,
  faPencilAlt,
  faUserCircle,
  faUserMinus,
  faCopy,
  faEllipsisV,
  faCaretRight,
  faEyeSlash,
  faComments
} from "@fortawesome/free-solid-svg-icons"
import SaveIcon from "@/components/save-icon"
import { Session, SURVEY_ROLES } from "@/redux/_session.types"
import ListIcon from "@/components/list-icon"
import ModalConfirm from "@/components/modal-confirm"
import Card from "@/components/card"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVED, STATUS_SAVING, STATUS_SEARCHING } from "@/redux/_status.types"
import PageLoader from "@/components/page-loader"
import { toast } from "react-toastify"
import Chip from "@/components/chip"
import { upperFirst } from "lodash"
import SelectPopulationsModal from "@/modals/select-populations.modal"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import AdmEditUserModal from "@/modals/adm-edit-user.modal"
import ListContainer from "@/components/list-container"
import UserPopulations from "@/components/user-populations"
import { AccountState } from "@/redux/account.types"
import { feedbackSend } from "@/redux/feedback.actions"
import LoadingModal from "@/modals/loading.modal"
import { Population } from "@/redux/population.types"
import SendInvitationButton from "@/components/SendInvitationButton"
import copyLink from "@/utils/copy-link.utils"
import { sessionEdit } from "@/redux/_session.actions"
import AdmSendEmailModal from "@/modals/adm-send-email.modal"

interface StateProps {
  _session: Session
  account: AccountState
  user: UserState
}

type Props = StateProps & WithTranslation

//Actions for button
const ACTION_COPY_LINK = "ACTION_COPY_LINK" //Copy invitation link
const ACTION_EDIT = "ACTION_EDIT" //Edit user
const ACTION_FEEDBACK = "ACTION_FEEDBACK" //Get user feedback
const ACTION_PREVIEW = "ACTION_PREVIEW" //Preview dashboard use
const ACTION_REMOVE = "ACTION_REMOVE" //Remove from roles list

//Modales
const MODAL_CONFIRM_REMOVE_ROLE = "MODAL_CONFIRM_REMOVE_ROLE" //Confirm removing rol
const MODAL_EDIT_USER = "MODAL_EDIT_USER" //Edit user informations
const MODAL_LOADING = "MODAL_LOADING" //Loading modal
const MODAL_SELECT_POPULATIONS = "MODAL_SELECT_POPULATIONS" //Select populations for dashboard
const MODAL_SEND_EMAIL = "MODAL_SEND_EMAIL" //Confirmation before send email
const MODAL_SEND_FEEDBACK = "MODAL_SEND_FEEDBACK" //Send feedback to admins

//Limit of users to display by page
const LIMIT: number = 10

function AdmRolesWidget(props: Props) {
  const { t } = props
  const isAdministratorsView = Boolean(props._session.accountOptions.roleManagementEnabled)

  //List of actions available
  const actions = initActions()

  //Current modal to display
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  //Current offset for search (when page is changed)
  const [currentOffset, setCurrentOffset] = useState<number>(0)

  //Content for searchbar
  const [currentSearch, setCurrentSearch] = useState<string>("")

  //User to edit (for populations)
  const [currentUser, setCurrentUser] = useState(new User())

  //Data is loading
  const [isLoading, setIsLoading] = useState<boolean>(true)

  //Get search count of admins
  const [searchCount, setSearchCount] = useState<number>(0)

  //Load data
  //Fetch all admin for account
  useEffect(() => {
    load()
    async function load() {
      setIsLoading(true)

      const admins: any = await store.dispatch(
        userFetch(
          isAdministratorsView ? "" : currentSearch,
          isAdministratorsView ? null : LIMIT,
          isAdministratorsView ? null : currentOffset,
          [isAdministratorsView ? "ADMIN" : props._session.selectedRole],
          "id,email,firstname,lastname,role"
        )
      )

      store.dispatch(userGet(admins.error ? [] : admins))
      store.dispatch(userStatus(STATUS_LOADED))
      setIsLoading(false)
    }
  }, [
    props.account.active.id,
    props._session.interfaceType,
    currentOffset,
    currentSearch,
    props._session.selectedRole,
    isAdministratorsView
  ])

  useEffect(() => {
    if (isAdministratorsView) return
    updateAdminCount()
    async function updateAdminCount() {
      const response: any = await store.dispatch(
        userFetchCount(
          currentSearch,
          props._session.accountOptions.roleManagementEnabled ? ["ADMIN"] : ["ADMIN", "OBSERVER"]
        )
      )
      setSearchCount(response.error ? 0 : response.count)
    }
  }, [currentSearch, props._session.accountOptions.roleManagementEnabled, isAdministratorsView])

  //Check if user is connected
  //Can not change role for connected user
  function isConnectedUser(userId: string) {
    return userId === props._session.userId
  }

  //Open select population modal
  function editPopulationsObserver(user: User) {
    setCurrentUser(user)
    setCurrentModal(MODAL_SELECT_POPULATIONS)
  }

  //Init email code for the user
  function getEmailCode() {
    return currentUser.role ? "inviteSurvey" + upperFirst(currentUser.role.toLowerCase()) : "inviteAdm"
  }

  //Init actions
  //Specic lines for supervisors
  function initActions() {
    const list = [
      { id: ACTION_PREVIEW, label: t("roles_preview"), icon: faEye },
      { id: ACTION_COPY_LINK, label: t("utils_copy_link"), icon: faCopy },
      { id: ACTION_EDIT, label: t("user_edit"), icon: faPencilAlt },
      { id: ACTION_REMOVE, label: t("roles_remove"), icon: faUserMinus }
    ]

    if (props._session.connectedAsSupervisor) {
      list.unshift({ id: ACTION_FEEDBACK, label: t("roles_feedback"), icon: faComments })
    }

    return list
  }

  //Open dashboard
  async function preview(user: User) {
    let error = false

    //Reset the password for the user (temp effect in order to access the dashboard)
    if (user.passwordProvided) {
      setCurrentModal(MODAL_LOADING)
      const response: any = await store.dispatch(userUpdatePasswordProvided_AsAdmin(user.id))
      error = response.error ? true : false
      setCurrentModal(null)
    }

    if (!error) {
      window.open(user.getInvitationLink(props._session, null, true), "_blank")
    }
  }

  //Select an action from context menu
  function selectAction(user: User, action: string) {
    setCurrentUser(user)
    store.dispatch(userActivate(user))
    switch (action) {
      case ACTION_COPY_LINK:
        return copyLink(user, props._session)
      case ACTION_FEEDBACK:
        return setCurrentModal(MODAL_SEND_FEEDBACK)
      case ACTION_REMOVE:
        return setCurrentModal(MODAL_CONFIRM_REMOVE_ROLE)
      case ACTION_PREVIEW:
        return preview(user)
      case ACTION_EDIT:
        return setCurrentModal(MODAL_EDIT_USER)
    }
  }

  //Click on sendEmail button (open dialog)
  function sendEmail(user: User) {
    setCurrentUser(user)
    setCurrentModal(MODAL_SEND_EMAIL)
  }

  //Send feedback to the user
  async function sendFeedback() {
    setCurrentModal(null)
    const response: any = await store.dispatch(feedbackSend(currentUser.id))
    if (!response.error) {
      toast(t("utils_email_send", { email: currentUser.email }))
    }
  }

  //Remove admin from the list
  async function removeRole() {
    setCurrentModal(null)

    store.dispatch(userStatus(STATUS_SAVING))
    let response: any = {}
    if (props._session.interfaceType === "ADMIN_PARENT") {
      response = await store.dispatch(userDestroy(currentUser.id))
    } else {
      response = await store.dispatch(userUpdateRole(Object.assign({}, currentUser, { role: null })))
    }

    store.dispatch(userStatus(STATUS_SAVED))
    if (!response.error) {
      store.dispatch(userRemove(currentUser.id))
    }
  }

  const visibleUsers = props.user.list.filter(
    (x) => x.role === (isAdministratorsView ? "ADMIN" : props._session.selectedRole)
  )

  //Une ligne seule dans sa carte : le trait de separation n'a rien a separer.
  const isRowAlone = isAdministratorsView && visibleUsers.length < 2

  const listContent = (
    <PageLoader
      status={isLoading ? STATUS_LOADING : STATUS_LOADED}
      className={isAdministratorsView ? "flex-jcenter" : undefined}>
      {props.user.list.length === 0 && (
        <ListItem isWithoutBorder={isRowAlone}>
          {!isAdministratorsView && currentSearch.length > 0
            ? t("search_no_result", { search: currentSearch })
            : t("roles_empty", {
                role: t("roles_" + props._session.selectedRole?.toLowerCase()).toLowerCase()
              })}
        </ListItem>
      )}

      {visibleUsers.map((user: User) => (
        <ListItem key={user.id} isWithoutBorder={isRowAlone}>
          <ListIcon fallbackIcon={faUserCircle} image={user.imageUrl} />

          <ListCell width={170} text={user.username} />

          {isAdministratorsView ? (
            <div className="flex flex1 items-center gap-2">
              <span className="flex1 grey-t" style={{ overflowWrap: "anywhere" }}>
                {user.email}
              </span>
              {!isConnectedUser(user.id) && (
                <>
                  <SaveIcon status={currentUser.id === user.id ? props.user.status : null} />
                  <SendInvitationButton user={user} onClick={() => sendEmail(user)} />
                  <ListButton
                    onAction={(action: string) => selectAction(user, action)}
                    icon={faEllipsisV}
                    actions={actions}
                  />
                </>
              )}
            </div>
          ) : isConnectedUser(user.id) && user.role === "ADMIN" ? (
            <Chip color={props._session.accountColors.active} icon={faCrown}>
              {t("roles_admin")}
            </Chip>
          ) : (
            <div className="flex flex1">
              {user.role === "ADMIN" && (
                <Chip color={props._session.accountColors.active} icon={faCrown}>
                  {t("roles_admin")}
                </Chip>
              )}

              <div className="width-10" />

              <div className="flex1">
                <UserPopulations
                  populations={user.populationsObserver}
                  emptyLabel={user.role === "OBSERVER" ? props._session.accountName : null}
                  onClick={() => editPopulationsObserver(user)}
                />

                {user.excludedFilters.length > 0 && (
                  <div
                    className="grey-t _hover"
                    onClick={() => editPopulationsObserver(user)}
                    style={{ fontSize: 12, marginTop: 8 }}>
                    <div>
                      <FontAwesomeIcon icon={faEyeSlash} />
                      &nbsp;
                      <b>Filtres masqués</b>
                    </div>
                    {user.excludedFilters.map((filterName, i) => (
                      <div key={i}>
                        <FontAwesomeIcon icon={faCaretRight} />
                        &nbsp;
                        {filterName}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="width-10" />

              <SaveIcon status={currentUser.id === user.id ? props.user.status : null} />

              <SendInvitationButton user={user} onClick={() => sendEmail(user)} />

              <ListButton
                onAction={(action: string) => selectAction(user, action)}
                icon={faEllipsisV}
                actions={actions}
              />
            </div>
          )}
        </ListItem>
      ))}
    </PageLoader>
  )

  return (
    <div>
      {!props._session.accountOptions.roleManagementEnabled && (
        <div className="flex flex-wrap" style={{ marginBottom: 12 }}>
          {SURVEY_ROLES.map((role, i) => {
            const isRoleActive = role === props._session.selectedRole
            return (
              <div
                key={i}
                className={"filters-item flex " + (isRoleActive ? "filters-item-active" : "filters-item-activable")}
                onClick={() => store.dispatch(sessionEdit("selectedRole", role))}
                style={{
                  color: isRoleActive ? props._session.accountColors.active : ""
                }}>
                {t("roles_" + role.toLowerCase())}
              </div>
            )
          })}
        </div>
      )}

      <Card isWithoutPadding>
        {currentModal === MODAL_LOADING && <LoadingModal />}

        {
          /** confirm removing permissions */
          currentModal === MODAL_CONFIRM_REMOVE_ROLE && (
            <ModalConfirm
              status={props.user.status}
              onNo={() => setCurrentModal(null)}
              onYes={() => removeRole()}
              text={t("user_admin_remove_confirm", { name: currentUser.username })}
              textBold={t("utils_next_ask")}
            />
          )
        }

        {currentModal === MODAL_EDIT_USER && (
          <AdmEditUserModal
            userToUpdate={currentUser}
            onClose={() => setCurrentModal(null)}
            onNext={() => setCurrentModal(null)}
          />
        )}

        {currentModal === MODAL_SELECT_POPULATIONS && (
          <SelectPopulationsModal
            allowNoFilter
            excludedFilters={currentUser.excludedFilters}
            isWithoutDefaultFilters
            isSurveyFiltersIncluded
            isAccountSelectable
            isObserverSurveyRestriction={props._session.selectedRole === "OBSERVER"}
            onClose={() => setCurrentModal(null)}
            selectedPopulations={currentUser.populationsObserver}
            restrictedSurveys={currentUser.options.restrictedSurveys}
            onNext={(populations: Population[], excludedFilters: string[], restrictedSurveys: string[]) =>
              store.dispatch(
                userEditAndUpdateRole(
                  new User(currentUser),
                  currentUser.role,
                  populations,
                  excludedFilters,
                  restrictedSurveys
                )
              )
            }
            title={t("roles_select_populations", {
              name: currentUser.username
            })}
          />
        )}

        {currentModal === MODAL_SEND_EMAIL && (
          <AdmSendEmailModal
            emailCode={getEmailCode()}
            currentUser={currentUser}
            onClose={() => setCurrentModal(null)}
            textBold={t("utils_next_ask")}
            text={t("utils_email_ask_confirm", {
              email: currentUser.email,
              name: currentUser.username
            })}
          />
        )}

        {currentModal === MODAL_SEND_FEEDBACK && (
          <ModalConfirm
            onNo={() => setCurrentModal(null)}
            onYes={sendFeedback}
            text={t("utils_feedback_ask_confirm", {
              email: currentUser.email,
              name: currentUser.username
            })}
            textBold={t("utils_next_ask")}
          />
        )}

        {isAdministratorsView ? (
          listContent
        ) : (
          <ListContainer
            countLimit={LIMIT}
            countSearch={searchCount}
            isFooterActive
            onAddTitle={t("user_add")}
            onSearch={props._session.accountOptions.roleManagementEnabled ? undefined : setCurrentSearch}
            onSearchStart={() => store.dispatch(userStatus(STATUS_SEARCHING))}
            onSetPage={setCurrentOffset}
            status={props.user.status}>
            {listContent}
          </ListContainer>
        )}
      </Card>
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  account: state.account,
  user: state.user
})

export default connect(mapStateToProps)(withTranslation()(AdmRolesWidget))
